import React, { Suspense, useCallback } from 'react';
import { prop } from 'ramda';
import { usePushSubscription } from 'server';
import { useTranslations } from 'js/hooks/i18n';
import { ManagedPopup } from 'js/components/common/Popup';
import { useOnClose } from 'js/components/common/PopupManager';
import { useAppSelector, useUserSelector } from 'js/store/hooks';
import { useShowNotification } from 'js/hooks/useShowNotification';
import { DynamicChangeDetails } from 'js/components/main/DynamicChangeDetails';

import { useChangeDetails } from './hooks/useChangeDetails';
import { NO_NEED_VERIFICATION_STATUSES } from './constants';

import style from './DataVerificationStatusPopup.scss';

export const DataVerificationStatusPopup: React.FC<{ id: string }> = ({ id }) => {
    const onClose = useOnClose(id);
    const showNotification = useShowNotification();
    const { ageVerificationStatus } = useUserSelector(prop('userDetails'));
    const translate = useTranslations('playtech.mobile.system.dataVerificationStatus.');
    const dataVerificationStatusFormName = useAppSelector(prop('dataVerificationStatusFormName'));

    const onSuccess = useCallback(() => {
        onClose();
        showNotification(translate('successVerification'), { variant: 'success' });
    }, [onClose, showNotification, translate]);

    const changeDetails = useChangeDetails({ onSuccess });

    usePushSubscription(31133, () => (pushMessage: any) => {
        const { playerData = {} } = pushMessage?.data?.action?.actionPlayerInfoChangeInfo || {};

        if (NO_NEED_VERIFICATION_STATUSES.includes(playerData.ageVerificationStatus)) {
            onSuccess();
        }
    }, [onSuccess]);

    return (
        <ManagedPopup
            id={id}
            isModal
            title={translate('title')}
            classNames={{ inner: style.innerContent }}
            layerClassNames={{ layout: style.layout }}
        >
            <Suspense fallback="">
                <p className={style.info}>{translate(`info.${ageVerificationStatus}`)}</p>
                <DynamicChangeDetails
                    formSubmit={changeDetails}
                    form={dataVerificationStatusFormName}
                />
            </Suspense>
        </ManagedPopup>
    );
};
